import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Target, Eye, Award, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default CMS content
const defaultContent = {
  hero: {
    title: 'About',
    highlight: 'PEA PNG',
    description: 'We are the voice of public employees across Papua New Guinea, committed to advocating for fair treatment, professional development, and better working conditions.',
  },
  mission: {
    text: 'To represent and advocate for the rights, welfare, and professional interests of public employees throughout Papua New Guinea, ensuring fair treatment, competitive compensation, and safe working environments for all members.'
  },
  vision: {
    text: 'To be the leading force in shaping a progressive and equitable public service sector in Papua New Guinea, where every employee is valued, empowered, and provided with opportunities for growth and development.'
  },
  values: {
    integrity_title: 'Integrity',
    integrity_text: 'We uphold the highest standards of honesty, transparency, and ethical conduct in all our interactions.',
    unity_title: 'Unity',
    unity_text: 'Together we stand stronger. We believe in the power of collective voice and collaborative action.',
    excellence_title: 'Excellence',
    excellence_text: 'We strive for excellence in representing our members and delivering value through our services.'
  }
};

const AboutPage = () => {
  const [leadership, setLeadership] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leadershipRes, contentRes] = await Promise.all([
        axios.get(`${API}/leadership`),
        axios.get(`${API}/page-content/about`)
      ]);
      setLeadership(leadershipRes.data);
      
      // Set page content from CMS
      const next = { ...defaultContent };
      contentRes.data.forEach(item => {
        if (item.section_name && item.content) {
          next[item.section_name] = { ...next[item.section_name], ...item.content };
        }
      });
      setContent(next);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section - CMS Driven */}
      <section className="bg-gradient-to-br from-orange-50 via-blue-50 to-green-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {content.hero.title} <span className="text-orange-500">{content.hero.highlight}</span>
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            {content.hero.description}
          </p>
        </div>
      </section>

      {/* Mission & Vision Section - CMS Driven */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 card-hover">
              <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <Target className="text-white" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-700 leading-relaxed">
                {content.mission.text}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 card-hover">
              <div className="w-16 h-16 bg-blue-400 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <Eye className="text-white" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h2>
              <p className="text-gray-700 leading-relaxed">
                {content.vision.text}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section - CMS Driven */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-gray-600">The principles that guide everything we do</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-md card-hover text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Award className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{content.values.integrity_title}</h3>
              <p className="text-gray-600 text-sm">
                {content.values.integrity_text}
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-md card-hover text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-300 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Users className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{content.values.unity_title}</h3>
              <p className="text-gray-600 text-sm">
                {content.values.unity_text}
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-md card-hover text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-300 to-green-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Target className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{content.values.excellence_title}</h3>
              <p className="text-gray-600 text-sm">
                {content.values.excellence_text}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Leadership</h2>
            <p className="text-gray-600">Meet the dedicated team leading our association</p>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : leadership.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {leadership.map((leader) => (
                <div
                  key={leader.id}
                  data-testid={`leader-card-${leader.id}`}
                  className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 card-hover text-center"
                >
                  <div className="w-32 h-32 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
                    {leader.image_url ? (
                      <img
                        src={leader.image_url}
                        alt={leader.name}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <span className="text-4xl font-bold text-white">
                        {leader.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{leader.name}</h3>
                  <p className="text-orange-500 font-semibold mb-3">{leader.position}</p>
                  <p className="text-sm text-gray-600">{leader.bio}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>Leadership information will be updated soon.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
